import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft,
  Loader2,
  BarChart3,
  Clock,
  MousePointer,
  RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsSimple = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ seven_day_totals: [], recent_events: [] });

  useEffect(() => { fetchAnalytics(); }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/analytics/cta-clicks`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const result = await res.json();
        setData(result);
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getCTAColor = (ctaKey) => {
    if (ctaKey?.includes('whatsapp')) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (ctaKey?.includes('call')) return 'bg-slate-100 text-slate-700 border-slate-200';
    if (ctaKey?.includes('program') || ctaKey?.includes('enroll')) return 'bg-primary/10 text-primary border-primary/20';
    if (ctaKey?.includes('pdf') || ctaKey?.includes('download')) return 'bg-amber-100 text-amber-700 border-amber-200';
    if (ctaKey?.includes('partner')) return 'bg-purple-100 text-purple-700 border-purple-200';
    return 'bg-slate-100 text-slate-600 border-slate-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto p-6" data-testid="analytics-simple">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">CTA Analytics</h1>
            <p className="text-sm text-slate-500">Simple click tracking for conversion CTAs</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchAnalytics}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* 7-Day Totals */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Last 7 Days Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.seven_day_totals.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.seven_day_totals.map((item, idx) => (
                <div key={idx} className="p-4 bg-slate-50 rounded-lg flex items-center justify-between">
                  <div>
                    <Badge className={getCTAColor(item.cta_key)}>
                      {item.cta_key?.replace(/_/g, ' ') || 'unknown'}
                    </Badge>
                    <p className="text-xs text-slate-500 mt-1">{item.page}</p>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{item.count}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-400 py-8">No clicks recorded in the last 7 days</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Events */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MousePointer className="w-5 h-5 text-primary" />
            Recent Clicks (Last 20)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.recent_events.length > 0 ? (
            <div className="space-y-2">
              {data.recent_events.map((event, idx) => (
                <div key={event.id || idx} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg text-sm">
                  <div className="flex items-center gap-2 text-slate-400 min-w-[140px]">
                    <Clock className="w-4 h-4" />
                    {formatDate(event.created_at)}
                  </div>
                  <Badge className={getCTAColor(event.cta_key)}>
                    {event.cta_key?.replace(/_/g, ' ') || 'unknown'}
                  </Badge>
                  <span className="text-slate-600 truncate">{event.label}</span>
                  <span className="text-slate-400 text-xs truncate hidden sm:block">{event.page}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-400 py-8">No recent clicks recorded</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsSimple;
